import GreyBlackField from "components/DataDisplay/GreyBlackField";
import { Box, FormControl, FormLabel, HStack, Icon, Input, Stack, Switch } from "@chakra-ui/react";
import CDS from "design-system";
import { useEffect } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import NewCaminoOption from "components/Selectable/NewCaminoOption";
import { FiSunrise, FiSunset } from "react-icons/fi";
import { WiDaySunny } from "react-icons/wi";

const DEFAULT_SPECIFIC_TIME = "09:00";

const options = [
  {
    value: "morning",
    label: "Morning",
    description: "9am - 12pm",
    icon: <Icon as={FiSunrise} boxSize={5} />,
  },
  {
    value: "midday",
    label: "Midday",
    description: "11am - 2pm",
    icon: <Icon as={WiDaySunny} boxSize={5} />,
  },
  {
    value: "afternoon",
    label: "Afternoon",
    description: "2pm - 5pm",
    icon: <Icon as={FiSunset} boxSize={5} />,
  },
];

interface MeetingTemplateTimingFieldProps {
  form: UseFormReturn<any>;
  disabled?: boolean;
  onChangeCallback?: () => void;
}

export default function MeetingTemplateTimeSlotField({
  form,
  disabled,
  onChangeCallback,
}: MeetingTemplateTimingFieldProps) {
  const { control, watch, setValue, getValues } = form;
  const useSpecificTime = watch("useSpecificTime");

  // Seed the visible default into form state so a user who toggles specific
  // time on and saves without touching the picker passes validation.
  useEffect(() => {
    if (useSpecificTime && !getValues("specificTimeOfDay")) {
      setValue("specificTimeOfDay", DEFAULT_SPECIFIC_TIME, { shouldDirty: false });
    }
  }, [useSpecificTime, getValues, setValue]);

  return (
    <Stack spacing={4}>
      <FormControl display="flex" alignItems="center">
        <FormLabel htmlFor="use-specific-time" mb="0">
          <CDS.Text12>Use specific time</CDS.Text12>
        </FormLabel>
        <Controller
          control={control}
          name="useSpecificTime"
          render={({ field }) => (
            <Switch
              id="use-specific-time"
              colorScheme="brand"
              isChecked={field.value}
              onChange={(e) => {
                field.onChange(e.target.checked);
                if (e.target.checked) {
                  setValue("preferredTimeSlot", null, { shouldDirty: true });
                } else {
                  setValue("specificTimeOfDay", null, { shouldDirty: true });
                  setValue("preferredTimeSlot", "morning", { shouldDirty: true });
                }
                onChangeCallback?.();
              }}
              isDisabled={disabled}
            />
          )}
        />
      </FormControl>

      {useSpecificTime ? (
        <GreyBlackField label="Specific Time" labelProps={{ marginBottom: 0 }}>
          <Box pt="2" px="1">
            <Controller
              control={control}
              name="specificTimeOfDay"
              render={({ field }) => (
                <HStack spacing={2}>
                  <Input
                    type="time"
                    value={field.value || DEFAULT_SPECIFIC_TIME}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      onChangeCallback?.();
                    }}
                    disabled={disabled}
                    width="140px"
                    borderColor="gray.300"
                    borderRadius="md"
                    _hover={{ borderColor: "gray.400" }}
                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
                  />
                  <CDS.Text12 color="gray.500">
                    (auto-scheduled)
                  </CDS.Text12>
                </HStack>
              )}
            />
          </Box>
        </GreyBlackField>
      ) : (
        <GreyBlackField label="Time of Day" labelProps={{ marginBottom: 0 }}>
          <Box pt="2">
            <Controller
              control={control}
              name="preferredTimeSlot"
              render={({ field }) => (
                <Stack>
                  <HStack spacing={4}>
                    {options.map((opt) => (
                      <NewCaminoOption
                        key={opt.value}
                        value={opt.value}
                        isChecked={field.value === opt.value}
                        width="140px"
                        onChange={() => {
                          field.onChange(opt.value);
                          onChangeCallback?.();
                        }}
                        label={opt.label}
                        description={opt.description}
                        icon={opt.icon}
                      />
                    ))}
                  </HStack>
                </Stack>
              )}
            />
          </Box>
        </GreyBlackField>
      )}
    </Stack>
  );
}
